import { addDays, differenceInCalendarDays, isAfter } from "date-fns";
import {
  DemandForecastCalendarBucket,
  DemandForecastCalendarEntry,
  DemandForecastCalendarPeriod,
  DemandForecastQuantityTotal,
  ItemUnit,
} from "@forgepoint/shared";
import {
  formatDateInput,
  formatQuantityWithUnit,
  parseDateInput,
} from "./utils";

const STACK_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

export interface ChartSegment {
  key: string;
  demandForecastId: string;
  label: string;
  detail: string;
  reasoning: string | null;
  locationName: string | null;
  weightedRevenue: number;
  weightedQuantity: number;
  quantityUnit: ItemUnit;
  probability: number;
  color: string;
}

export interface PeriodDemand {
  key: string;
  label: string;
  weightedRevenue: number;
  totalRevenue: number;
  quantityTotals: DemandForecastQuantityTotal[];
  weightedQuantityTotals: DemandForecastQuantityTotal[];
  segments: ChartSegment[];
}

interface DemandSummary {
  weightedRevenue: number;
  totalRevenue: number;
  quantityTotals: DemandForecastQuantityTotal[];
  weightedQuantityTotals: DemandForecastQuantityTotal[];
  avgProbability: number;
}

interface PreviousDateRange {
  startDate: string;
  endDate: string;
}

export function buildPeriodDemand(
  calendarBuckets: DemandForecastCalendarPeriod[],
  startDate: string,
  endDate: string,
  bucket: DemandForecastCalendarBucket,
): PeriodDemand[] {
  const periods = buildEmptyPeriods(startDate, endDate, bucket);
  const periodMap = new Map(periods.map((period) => [period.key, period]));

  calendarBuckets.forEach((calendarBucket) => {
    const periodKey = getDateKey(
      getFirstPeriodStart(calendarBucket.periodStart, bucket),
    );
    const period = periodMap.get(periodKey);
    if (!period) {
      return;
    }

    period.weightedRevenue += getNumericValue(calendarBucket.weightedRevenue);
    period.totalRevenue += getNumericValue(calendarBucket.totalRevenue);
    period.quantityTotals = mergeQuantityTotals(
      period.quantityTotals,
      calendarBucket.quantityTotals,
    );
    period.weightedQuantityTotals = mergeQuantityTotals(
      period.weightedQuantityTotals,
      calendarBucket.weightedQuantityTotals,
    );
    calendarBucket.entries.forEach((entry) => {
      mergeChartSegment(period.segments, entry);
    });
  });

  periods.forEach((period) => {
    period.segments.sort(
      (a, b) => a.color.localeCompare(b.color) || a.key.localeCompare(b.key),
    );
  });

  return periods;
}

export function getPreviousDateRange(
  startDate: string,
  endDate: string,
): PreviousDateRange | null {
  if (!startDate || !endDate) {
    return null;
  }

  const parsedStart = parseDateInput(startDate);
  const parsedEnd = parseDateInput(endDate);
  const dayCount = differenceInCalendarDays(parsedEnd, parsedStart) + 1;
  if (dayCount <= 0) {
    return null;
  }

  const previousEnd = addDays(parsedStart, -1);
  const previousStart = addDays(previousEnd, -(dayCount - 1));
  return {
    startDate: formatDateInput(previousStart),
    endDate: formatDateInput(previousEnd),
  };
}

export function summarizeDemand(periodDemand: PeriodDemand[]): DemandSummary {
  const weightedRevenue = periodDemand.reduce(
    (total, period) => total + period.weightedRevenue,
    0,
  );
  const totalRevenue = periodDemand.reduce(
    (total, period) => total + period.totalRevenue,
    0,
  );
  const quantityTotals = periodDemand.reduce(
    (totals, period) => mergeQuantityTotals(totals, period.quantityTotals),
    [] as DemandForecastQuantityTotal[],
  );
  const weightedQuantityTotals = periodDemand.reduce(
    (totals, period) =>
      mergeQuantityTotals(totals, period.weightedQuantityTotals),
    [] as DemandForecastQuantityTotal[],
  );
  const avgProbability =
    totalRevenue > 0 ? (weightedRevenue / totalRevenue) * 100 : 0;

  return {
    weightedRevenue,
    totalRevenue,
    quantityTotals,
    weightedQuantityTotals,
    avgProbability,
  };
}

export function getQuantityByUnit(
  quantityTotals: DemandForecastQuantityTotal[],
  quantityUnit: ItemUnit,
): number {
  return getNumericValue(
    quantityTotals.find((total) => total.quantityUnit === quantityUnit)
      ?.quantity ?? "0",
  );
}

export function getPrimaryVolume(summary: DemandSummary): {
  quantity: number;
  quantityUnit: ItemUnit;
} | null {
  const primaryTotal = summary.quantityTotals[0];
  if (!primaryTotal) {
    return null;
  }

  return {
    quantity: getNumericValue(primaryTotal.quantity),
    quantityUnit: primaryTotal.quantityUnit,
  };
}

export function getPercentDelta(
  current: number,
  previous: number,
): number | null {
  if (previous === 0) {
    return null;
  }
  return ((current - previous) / previous) * 100;
}

export function formatDelta(delta: number | null): string {
  if (delta === null) {
    return "No prior range";
  }
  const sign = delta > 0 ? "+" : "";
  return `${sign}${delta.toFixed(1)}% vs prior range`;
}

export function formatQuantityTotals(
  quantityTotals: DemandForecastQuantityTotal[],
) {
  return (
    quantityTotals
      .map((total) =>
        formatQuantityWithUnit(total.quantity, total.quantityUnit),
      )
      .join(", ") || "No volume"
  );
}

function buildEmptyPeriods(
  startDate: string,
  endDate: string,
  bucket: DemandForecastCalendarBucket,
): PeriodDemand[] {
  if (!startDate || !endDate) {
    return [];
  }

  const firstPeriodStart = getFirstPeriodStart(
    parseCalendarDateInput(startDate),
    bucket,
  );
  const lastPeriodStart = getFirstPeriodStart(
    parseCalendarDateInput(endDate),
    bucket,
  );
  const periods: PeriodDemand[] = [];

  for (
    let cursor = firstPeriodStart;
    !isAfter(cursor, lastPeriodStart);
    cursor = getNextPeriod(cursor, bucket)
  ) {
    periods.push({
      key: getDateKey(cursor),
      label: getPeriodLabel(cursor, bucket),
      weightedRevenue: 0,
      totalRevenue: 0,
      quantityTotals: [],
      weightedQuantityTotals: [],
      segments: [],
    });
  }

  return periods;
}

/**
 * Adds an entry into the segment list, merging it with the existing segment for
 * the same forecast. A single forecast can span multiple lines within one
 * period (e.g. weekly lines straddling a calendar week boundary); those are
 * summed into one segment so the forecast renders as a single stacked band.
 * @param segments - The period's segments, mutated in place.
 * @param entry - The calendar entry to add or merge.
 */
function mergeChartSegment(
  segments: ChartSegment[],
  entry: DemandForecastCalendarEntry,
): void {
  const existing = segments.find(
    (segment) => segment.demandForecastId === entry.demandForecastId,
  );
  if (existing) {
    existing.weightedRevenue += getNumericValue(entry.weightedRevenue);
    existing.weightedQuantity += getNumericValue(entry.weightedQuantity);
    return;
  }

  segments.push({
    key: entry.demandForecastId,
    demandForecastId: entry.demandForecastId,
    label: entry.opportunityName,
    detail: entry.description,
    reasoning: entry.reasoning ?? null,
    locationName: entry.locationName,
    weightedRevenue: getNumericValue(entry.weightedRevenue),
    weightedQuantity: getNumericValue(entry.weightedQuantity),
    quantityUnit: entry.quantityUnit,
    probability: entry.probability,
    color: getStackColor(entry.demandForecastId),
  });
}

function mergeQuantityTotals(
  currentTotals: DemandForecastQuantityTotal[],
  nextTotals: DemandForecastQuantityTotal[],
): DemandForecastQuantityTotal[] {
  const totals = new Map<ItemUnit, number>();

  currentTotals.forEach((total) => {
    totals.set(total.quantityUnit, getNumericValue(total.quantity));
  });
  nextTotals.forEach((total) => {
    const currentQuantity = totals.get(total.quantityUnit) ?? 0;
    totals.set(
      total.quantityUnit,
      currentQuantity + getNumericValue(total.quantity),
    );
  });

  return Array.from(totals.entries()).map(([quantityUnit, quantity]) => ({
    quantityUnit,
    quantity: String(quantity),
  }));
}

function getNumericValue(value: string): number {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function getStackColor(key: string): string {
  const hash = Array.from(key).reduce(
    (total, character) => total + character.charCodeAt(0),
    0,
  );
  return STACK_COLORS[hash % STACK_COLORS.length];
}

function getDateKey(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getPeriodLabel(
  date: Date,
  bucket: DemandForecastCalendarBucket,
): string {
  if (bucket === "MONTH") {
    return formatUtcMonthYear(date);
  }
  if (bucket === "QUARTER") {
    const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
    return `Q${quarter} ${date.getUTCFullYear()}`;
  }
  const periodEnd = addUtcDays(date, 6);
  return `${formatUtcMonthDay(date)} - ${formatUtcMonthDay(periodEnd)}`;
}

function getFirstPeriodStart(
  date: Date,
  bucket: DemandForecastCalendarBucket,
): Date {
  if (bucket === "MONTH") {
    return startOfUtcMonth(date);
  }
  if (bucket === "QUARTER") {
    return startOfUtcQuarter(date);
  }
  return startOfUtcWeek(date);
}

function getNextPeriod(date: Date, bucket: DemandForecastCalendarBucket): Date {
  if (bucket === "MONTH") {
    return addUtcMonths(date, 1);
  }
  if (bucket === "QUARTER") {
    return addUtcMonths(date, 3);
  }
  return addUtcDays(date, 7);
}

function parseCalendarDateInput(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
    return new Date(Date.UTC(year, month - 1, day));
  }
  return new Date(value);
}

function startOfUtcMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function startOfUtcQuarter(date: Date): Date {
  const quarterMonth = Math.floor(date.getUTCMonth() / 3) * 3;
  return new Date(Date.UTC(date.getUTCFullYear(), quarterMonth, 1));
}

function startOfUtcWeek(date: Date): Date {
  const utcDate = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  const mondayOffset = (utcDate.getUTCDay() + 6) % 7;
  utcDate.setUTCDate(utcDate.getUTCDate() - mondayOffset);
  return utcDate;
}

function addUtcMonths(date: Date, months: number): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1),
  );
}

function addUtcDays(date: Date, days: number): Date {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() + days,
    ),
  );
}

function formatUtcMonthYear(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    timeZone: "UTC",
    year: "numeric",
  }).format(date);
}

function formatUtcMonthDay(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(date);
}
